import { useState } from 'react';
import { css } from '@emotion/react';
import {
  AnalyticsPageSubtitleWrapper,
  AnalyticsPageViewTabs,
  DemoDataBanner,
  GeneralFeedbackButton,
  pageStyles,
} from '@frontend/analytics-shared';
import { Header } from '@frontend/header-navigation';
import { useTranslation } from '@frontend/i18n';
import { Page } from '@frontend/page';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Tabs } from '@frontend/design-system';
import { AppointmentAnalyticsFilters } from '../components/appointment-analytics-filters';
import { AppointmentAnalyticsV2 } from '../components/appointment-analytics-v2';
import { AppointmentChartsView } from '../components/appointment-charts-view';
import { AppointmentTableView } from '../components/appointment-table-view';
import { useAppointmentAnalyticsShallowStore } from '../hooks/use-appointment-analytics-store';
import { useIsAppointmentDemoAccount } from '../hooks/use-is-appointment-demo-account';
import { useShowAppointmentAnalyticsV2 } from '../hooks/use-show-updated-appointment-analytics';
import { trackingIds } from '../tracking-ids';

const DEFAULT_VIEW = 'charts-view';

export const AppointmentAnalytics = () => {
  const { t } = useTranslation('analytics');
  const { filterHintText, filters } = useAppointmentAnalyticsShallowStore('filterHintText');
  const [activeTab, setActiveTab] = useState<string>(DEFAULT_VIEW);
  const [isFetchingChartsData, setIsFetchingChartsData] = useState<boolean>(false);
  const [isFetchingTableData, setIsFetchingTableData] = useState<boolean>(false);
  const isDemoAccount = useIsAppointmentDemoAccount();
  const showAppointmentAnalyticsV2 = useShowAppointmentAnalyticsV2();

  if (showAppointmentAnalyticsV2) {
    return <AppointmentAnalyticsV2 />;
  }

  return (
    <>
      {isDemoAccount && (
        <DemoDataBanner title={t("You're taking Appointment Analytics for a test drive with demo data!")} />
      )}
      <Tabs initialTab={DEFAULT_VIEW} onChange={setActiveTab}>
        <Page>
          <Page.Header>
            <Page.Header.Heading css={styles.pageHeading}>
              <Page.Header.Title className='header-title'>
                <Header title={t('Appointment Analytics')} />
                <GeneralFeedbackButton selectedLocationFromPageFilter={filters?.LocationID} />
              </Page.Header.Title>
              <AnalyticsPageSubtitleWrapper>
                <Page.Header.Subtitle
                  subtitle={t('Showing results for {{filtersInfo}}', {
                    filtersInfo: filterHintText,
                  })}
                />
                <AnalyticsPageViewTabs
                  renderContent={
                    <AppointmentAnalyticsFilters isLoadingData={isFetchingChartsData || isFetchingTableData} />
                  }
                  trackingIds={trackingIds}
                />
              </AnalyticsPageSubtitleWrapper>
            </Page.Header.Heading>
          </Page.Header>

          <Page.Body>
            <div css={pageStyles.panelsWrapper}>
              <Tabs.Panel controller='table-view' css={pageStyles.tabPanel}>
                <AppointmentTableView onFetchStateChange={setIsFetchingTableData} />
              </Tabs.Panel>
              <Tabs.Panel controller='charts-view' css={pageStyles.tabPanel}>
                {activeTab === 'charts-view' && <AppointmentChartsView onFetchStateChange={setIsFetchingChartsData} />}
              </Tabs.Panel>
            </div>
          </Page.Body>
        </Page>
      </Tabs>
    </>
  );
};

const styles = {
  pageHeading: css`
    .header-title {
      width: 100%;
      display: flex;
      justify-content: space-between;
      @container (max-width:  ${breakpoints.xsmall.max}px) {
        flex-direction: row;
      }
    }
    .page-action {
      @container (max-width:  ${breakpoints.xsmall.max}px) {
        width: 100%;
      }
    }
  `,
  actions: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(2)};
  `,
};
